// ═══════════════════════════════════════════════════════════
// QR Scanner — Camera-based QR code reading
// Uses html5-qrcode for persistent camera scanning
// ═══════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';
import { IconCamera } from '@tabler/icons-react';
import { notify } from '../gab';

interface QRScannerProps {
  onScan: (code: string) => void;
  active?: boolean;
}

export function QRScanner({ onScan, active = true }: QRScannerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const scannerRef = useRef<any>(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!active || !containerRef.current) return;

    let mounted = true;

    async function init() {
      try {
        const { Html5Qrcode } = await import('html5-qrcode');
        if (!mounted || !containerRef.current) return;

        const scanner = new Html5Qrcode(containerRef.current.id);
        scannerRef.current = scanner;

        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            onScan(decodedText);
          },
          () => {} // ignore errors (no QR found in frame)
        );
        setStarted(true);
      } catch (e) {
        notify.error('Camera access denied or QR scanner unavailable');
      }
    }

    init();

    return () => {
      mounted = false;
      scannerRef.current?.stop?.().catch(() => {});
      scannerRef.current = null;
      setStarted(false);
    };
  }, [active, onScan]);

  return (
    <div>
      <div
        id="qr-scanner-container"
        ref={containerRef}
        style={{
          width: '100%', maxWidth: 400, margin: '0 auto',
          borderRadius: 'var(--radius-lg)', overflow: 'hidden',
          minHeight: started ? 300 : 100,
          background: started ? '#000' : 'var(--color-neutral-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {!started && (
          <div className="text-center py-6">
            <IconCamera size={32} color="var(--color-neutral-400)" />
            <p className="text-xs text-muted mt-2">Initializing camera...</p>
          </div>
        )}
      </div>
    </div>
  );
}
